/**
 * Shared popup trigger for source links. Accepts a pre-mapped SourcesType[] array
 * and renders a clickable logo stack that opens a sources popup.
 */
import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import Popup from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import Pressable from '@atlaskit/primitives/pressable';
import { token } from '@atlaskit/tokens';
import { SourcesPopupContent } from '@atlassian/conversation-assistant-content-renderer/ui/message-renderer/SourcesPopupContent';
import type { Sources as SourcesType } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { SourceLogoStack } from '@atlassian/rovo-platform-ui-components/source-logo-stack';

import messages from './messages';

const ANALYTICS_CHANNEL = 'aiMate';

const styles = cssMap({
	triggerWrapper: {
		alignSelf: 'start',
	},
	pressable: {
		borderRadius: token('radius.medium'),
		border: 'none',
		backgroundColor: 'transparent',
		paddingBlock: token('space.0'),
		paddingInlineStart: token('space.025'),
		paddingInlineEnd: token('space.0'),
		cursor: 'pointer',
		'&:hover': {
			borderRadius: token('radius.medium'),
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	selectedPressable: {
		borderRadius: token('radius.medium'),
		border: `${token('border.width')} solid ${token('color.border.selected')}`,
		backgroundColor: token('color.background.selected.pressed'),
		paddingBlock: token('space.0'),
		paddingInlineStart: token('space.025'),
		paddingInlineEnd: token('space.0'),
		cursor: 'pointer',
	},
});

export interface SourcesPopupTriggerProps {
	sources: SourcesType;
	testId?: string;
}

/** A click-triggered logo stack that shows a popup listing all source links. */
export const SourcesPopupTrigger = ({
	sources,
	testId = 'rovo-for-you-sources-button',
}: SourcesPopupTriggerProps): React.JSX.Element | null => {
	const intl = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleSourceLinkClick = useCallback(
		(type: string, ari?: string) => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'sourceLink',
				attributes: {
					is3pLink: !ari,
					sourceType: type,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	const handleToggle = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'sources',
				attributes: { state: isOpen ? 'closed' : 'open' },
			}).fire(ANALYTICS_CHANNEL);
			setIsOpen((previousState) => !previousState);
		},
		[createAnalyticsEvent, isOpen],
	);

	if (sources.length === 0) {
		return null;
	}

	const sourceUrls = sources.map((source) => source.url);

	return (
		<Box xcss={styles.triggerWrapper}>
			<Popup
				placement="bottom-start"
				isOpen={isOpen}
				onClose={handleClose}
				content={() => (
					<SourcesPopupContent
						sources={sources}
						onClickSourceHandler={handleSourceLinkClick}
						onClose={handleClose}
					/>
				)}
				trigger={(triggerProps) => (
					<Pressable
						{...triggerProps}
						xcss={isOpen ? styles.selectedPressable : styles.pressable}
						testId={testId}
						aria-label={intl.formatMessage(messages.sourcesButtonLabel)}
						onClick={handleToggle}
					>
						<SourceLogoStack sources={sourceUrls} />
					</Pressable>
				)}
			/>
		</Box>
	);
};
